<script setup>
import { ref, onMounted, onBeforeUnmount, watch } from 'vue';
import {
  Chart,
  DoughnutController,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  buildDoughnutChartOptions,
  createConnectorLinesPlugin,
  DEFAULT_MIN_LABEL_PERCENTAGE,
} from './doughnutChartConfig';

const props = defineProps({
  collection: {
    type: Object,
    required: true,
  },
  chartOptions: {
    type: Object,
    default: () => ({}),
  },
  height: {
    type: String,
    default: '100%',
  },
  minLabelPercentage: {
    type: Number,
    default: DEFAULT_MIN_LABEL_PERCENTAGE,
  },
});

const emit = defineEmits(['slice-click']);

Chart.register(DoughnutController, ArcElement, Tooltip, Legend);

const chartContainer = ref(null);
let chartInstance = null;

const createChart = () => {
  if (!chartContainer.value) return;

  if (chartInstance) {
    chartInstance.destroy();
  }

  const ctx = chartContainer.value.getContext('2d');
  const options = buildDoughnutChartOptions({
    chartOptions: props.chartOptions,
    minLabelPercentage: props.minLabelPercentage,
    onClick: (evt, elements) => {
      if (!elements.length) return;
      const { index } = elements[0];
      const label = props.collection.labels?.[index];
      const value = props.collection.datasets?.[0]?.data?.[index];
      emit('slice-click', { index, label, value });
    },
  });

  chartInstance = new Chart(ctx, {
    type: 'doughnut',
    data: props.collection,
    options,
    plugins: [
      ChartDataLabels,
      createConnectorLinesPlugin({
        minPercentage: props.minLabelPercentage,
      }),
    ],
  });
};

watch(
  () => [props.collection, props.chartOptions, props.minLabelPercentage],
  () => {
    createChart();
  },
  { deep: true }
);

onMounted(() => {
  createChart();
});

onBeforeUnmount(() => {
  if (chartInstance) {
    chartInstance.destroy();
    chartInstance = null;
  }
});
</script>

<template>
  <div class="donut-chart-container" :style="{ height }">
    <canvas ref="chartContainer" />
  </div>
</template>

<style scoped>
.donut-chart-container {
  position: relative;
  width: 100%;
  cursor: pointer;
}

.donut-chart-container canvas {
  cursor: pointer;
}
</style>
